import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';
import { configureStore, combineReducers, createAction } from "@reduxjs/toolkit";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import sessionStorage from 'redux-persist/lib/storage/session';
import { getPersistConfig } from 'redux-deep-persist';
import authentication, { authenticationListener } from "./authentication";
import authorisation, { authorisationListener } from "./authorisation";
import company, { companyListener } from "./company";
import license, { licenseListener } from "./license";
import logger from './logger';
import { isDev } from 'config/constants';

export const resetApp = createAction(`RESET_APP`);

const reducer = combineReducers({
    [authentication.name]: authentication.reducer,
    [authorisation.name]: authorisation.reducer,
    [license.name]: license.reducer,
    [company.name]: company.reducer,
});

const persistConfig = getPersistConfig({
    key: "kdui-root",
    storage: sessionStorage,
    rootReducer: reducer,
    timeout: 0
})

const persistedReducer = persistReducer(
    persistConfig,
    reducer
);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => {
        let middleware = getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                ignoredActionPaths: ['register', 'rehydrate'],
            },
        })
            .concat(authenticationListener.middleware)
            .concat(authorisationListener.middleware)
            .concat(licenseListener.middleware)
            .concat(companyListener.middleware)

        if (isDev) {
            // @ts-ignore
            middleware.push(logger);
        }

        return middleware;
    },
    devTools: isDev
});

export const createStoreWithInitialState = (initialState?: any) => {
    const store = configureStore({
        ...(initialState ? { preloadedState: initialState } : {}),
        reducer: persistedReducer,
        middleware: (getDefaultMiddleware) => {
            let middleware = getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                    ignoredActionPaths: ['register', 'rehydrate'],
                },
            })
                .concat(authenticationListener.middleware)
                .concat(authorisationListener.middleware)
                .concat(licenseListener.middleware)
                .concat(companyListener.middleware)

            if (isDev) {
                // @ts-ignore
                middleware.push(logger);
            }

            return middleware;
        },
        devTools: isDev
    });
    const persistor = persistStore(store);
    return { store, persistor }
}

export type AppDispatch = typeof store.dispatch
export type ApplicationState = ReturnType<typeof store.getState>

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ApplicationState> = useSelector;

export default store;
